import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import PropertyCard from "./PropertyCard";
import { getAllProperties, propertiesToArray } from "../Functions/FetchProperties";

const VillaList = () => {
    const location = useLocation();
    const [properties, setProperties] = useState([]);
    const [filteredProperties, setFilteredProperties] = useState([]);
    const [locations, setLocations] = useState([]);
    const [amenityCategories, setAmenityCategories] = useState({});
    const [selectedLocation, setSelectedLocation] = useState("all");
    const [selectedCategories, setSelectedCategories] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchParams, setSearchParams] = useState({
        location: "",
        checkIn: "",
        checkOut: "",
        adults: 0,
        children: 0
    });
    
    // Parse URL search parameters
    useEffect(() => {
        const queryParams = new URLSearchParams(location.search);
        const newSearchParams = {
            location: queryParams.get('location') || "",
            checkIn: queryParams.get('checkIn') || "",
            checkOut: queryParams.get('checkOut') || "",
            adults: parseInt(queryParams.get('adults') || "0", 10),
            children: parseInt(queryParams.get('children') || "0", 10)
        };
        
        setSearchParams(newSearchParams);
        
        // Set initial location filter if provided in URL
        if (newSearchParams.location) {
            setSelectedLocation(newSearchParams.location);
        }
        
        // Apply filters if we already have properties loaded
        if (properties.length > 0 && newSearchParams.location) {
            applyUrlFilters(properties, newSearchParams.location);
        }
    }, [location.search, properties.length]);
    
    // Function to categorize amenities
    const categorizeAmenity = (amenity) => {
        const amenityLower = amenity.toLowerCase();
        
        // TV & Entertainment
        if (amenityLower.includes('tv') || 
            amenityLower.includes('smart') || 
            amenityLower.includes('entertainment') ||
            amenityLower.includes('carrom') ||
            amenityLower.includes('pool table') ||
            amenityLower.includes('music') || 
            amenityLower.includes('speaker') ||
            amenityLower.includes('game')) {
            return 'TV & Entertainment';
        }
        
        // Swimming & Pool
        if (amenityLower.includes('pool') || 
            amenityLower.includes('swimming')) {
            return 'Swimming & Pool';
        }
        
        // Climate Control
        if (amenityLower.includes('ac') || 
            amenityLower.includes('air condition') ||
            amenityLower.includes('geyser') ||
            amenityLower.includes('heater') ||
            amenityLower.includes('room-darkening') ||
            amenityLower.includes('temperature')) {
            return 'Climate Control';
        }
        
        // Kitchen & Appliances
        if (amenityLower.includes('kitchen') || 
            amenityLower.includes('fridge') ||
            amenityLower.includes('refrigerator') ||
            amenityLower.includes('microwave') ||
            amenityLower.includes('coffee') ||
            amenityLower.includes('induction') ||
            amenityLower.includes('washing machine') ||
            amenityLower.includes('dishwasher') ||
            amenityLower.includes('oven') ||
            amenityLower.includes('cookware')) {
            return 'Kitchen & Appliances';
        }
        
        // Views & Outdoor
        if (amenityLower.includes('view') || 
            amenityLower.includes('outdoor') ||
            amenityLower.includes('bbq') ||
            amenityLower.includes('grill') ||
            amenityLower.includes('dining area') ||
            amenityLower.includes('sitting arrangement') ||
            amenityLower.includes('garden') ||
            amenityLower.includes('balcony') ||
            amenityLower.includes('terrace') ||
            amenityLower.includes('patio')) {
            return 'Views & Outdoor';
        }
        
        // Internet & Connectivity
        if (amenityLower.includes('wifi') || 
            amenityLower.includes('internet') ||
            amenityLower.includes('high-speed') ||
            amenityLower.includes('workspace') ||
            amenityLower.includes('dedicated workspace')) {
            return 'Internet & Connectivity';
        }
        
        // Essentials & Convenience
        if (amenityLower.includes('bed linen') || 
            amenityLower.includes('towels') ||
            amenityLower.includes('soap') ||
            amenityLower.includes('paper') ||
            amenityLower.includes('storage') ||
            amenityLower.includes('clothes') ||
            amenityLower.includes('check-in') ||
            amenityLower.includes('lockbox') ||
            amenityLower.includes('care taker')) {
            return 'Essentials & Convenience';
        }
        
        // Parking & Transportation
        if (amenityLower.includes('parking') || 
            amenityLower.includes('car')) {
            return 'Parking & Transportation';
        }
        
        // Safety & Security
        if (amenityLower.includes('fire') || 
            amenityLower.includes('first aid') ||
            amenityLower.includes('extinguisher') ||
            amenityLower.includes('genset') ||
            amenityLower.includes('security')) {
            return 'Safety & Security';
        }
        
        // Fitness & Wellness
        if (amenityLower.includes('gym') || 
            amenityLower.includes('exercise') ||
            amenityLower.includes('fitness') ||
            amenityLower.includes('workout') ||
            amenityLower.includes('yoga')) {
            return 'Fitness & Wellness';
        }
        
        // Special Features
        if (amenityLower.includes('pet') || 
            amenityLower.includes('smoking') ||
            amenityLower.includes('restaurant') ||
            amenityLower.includes('standalone') ||
            amenityLower.includes('waterfront')) {
            return 'Special Features';
        }
        
        // If no category matched, put in "Other Amenities"
        return 'Other Amenities';
    };
    
    // Fetch properties on component mount
    useEffect(() => {
        const fetchProperties = async () => {
            try {
                setLoading(true);
                const propertiesData = await getAllProperties();
                const propertiesArray = propertiesToArray(propertiesData);
                
                // Extract and group locations
                const locationSet = new Set();
                
                // Group amenities by category
                const categorizedAmenities = {};
                
                propertiesArray.forEach(prop => {
                    // Process locations
                    if (prop.location) {
                        if (prop.location.toLowerCase().includes('pattaya') || 
                            prop.location.toLowerCase().includes('thailand')) {
                            locationSet.add('Pattaya, Thailand');
                        } else {
                            locationSet.add(prop.location);
                        }
                    }
                    
                    // Process amenities
                    if (prop.amenities && Array.isArray(prop.amenities)) {
                        prop.amenities.forEach(amenity => {
                            const category = categorizeAmenity(amenity);
                            
                            if (!categorizedAmenities[category]) {
                                categorizedAmenities[category] = new Set();
                            }
                            categorizedAmenities[category].add(amenity);
                        });
                    }
                    
                    // Add special amenities
                    if (prop.isStandalone) {
                        const category = 'Special Features';
                        if (!categorizedAmenities[category]) {
                            categorizedAmenities[category] = new Set();
                        }
                        categorizedAmenities[category].add('Standalone Property');
                    }
                    
                    if (prop.petFriendly) {
                        const category = 'Special Features';
                        if (!categorizedAmenities[category]) {
                            categorizedAmenities[category] = new Set();
                        }
                        categorizedAmenities[category].add('Pets Allowed');
                    }
                });
                
                // Convert Set objects to arrays
                const processedCategories = {};
                Object.keys(categorizedAmenities).forEach(category => {
                    processedCategories[category] = [...categorizedAmenities[category]].sort();
                });
                
                setProperties(propertiesArray);
                setLocations([...locationSet]);
                setAmenityCategories(processedCategories);
                
                // Apply URL search filters once properties are loaded
                if (searchParams.location) {
                    applyUrlFilters(propertiesArray, searchParams.location);
                } else {
                    // If no location filter, show all properties
                    setFilteredProperties(propertiesArray);
                }
            } catch (error) {
                console.error("Error fetching properties:", error);
            } finally {
                setLoading(false);
            }
        };
        
        fetchProperties();
    }, [searchParams]); // Add searchParams as a dependency
    
    // Apply filters from URL parameters
    const applyUrlFilters = (allProperties, locationParam) => {
        let result = [...allProperties];
        
        // Filter by location
        if (locationParam) {
            const locationLower = locationParam.toLowerCase();
            
            if (locationLower.includes('pattaya') || locationLower.includes('thailand')) {
                result = result.filter(property => 
                    property.location?.toLowerCase().includes('pattaya') || 
                    property.location?.toLowerCase().includes('thailand')
                );
            } else {
                // Use includes for more flexible matching instead of exact match
                result = result.filter(property => 
                    property.location?.toLowerCase().includes(locationLower)
                );
            }
            
            // Update radio buttons to match selected location
            setTimeout(() => {
                const locationInputs = document.querySelectorAll('input[name="location"]');
                let foundMatch = false;
                
                locationInputs.forEach(input => {
                    if (input.value.toLowerCase().includes(locationLower) || 
                        locationLower.includes(input.value.toLowerCase())) {
                        input.checked = true;
                        foundMatch = true;
                    }
                });
                
                // If no exact match was found but we have a location parameter,
                // uncheck the "All" option
                if (!foundMatch && locationParam) {
                    const allLocationInput = document.getElementById('locationAll');
                    if (allLocationInput) {
                        allLocationInput.checked = false;
                    }
                }
            }, 100);
        }
        
        // Filter by capacity if adults + children were specified
        if (searchParams.adults > 0 || searchParams.children > 0) {
            const totalGuests = searchParams.adults + searchParams.children;
            result = result.filter(property => {
                // Check if property can accommodate the requested number of guests
                return (property.maxOccupancy && property.maxOccupancy >= totalGuests) ||
                       (property.capacity && property.capacity >= totalGuests);
            });
        }
        
        setFilteredProperties(result);
    };
    
    // Convert amenity display name to value used in filtering
    const getAmenityValue = (amenity) => {
        // Map display names to filter values
        const amenityMap = {
            'Standalone Property': 'standalone',
            'Private Swimming Pool': 'privatePool',
            'Pool': 'pool',
            'Private Pool': 'privatePool',
            'Resort View': 'resortView',
            'Valley View': 'valleyView',
            'Garden View': 'gardenView',
            'Hill View': 'hillView',
            'Pool View': 'poolView',
            'Waterfront': 'waterfront',
            'Private Patio or Balcony': 'balcony',
            'Outdoor Dining Area': 'outdoorDining',
            'BBQ grill': 'bbq',
            'Free Parking on Premises': 'parking',
            'Free car parking': 'parking',
            'Pets Allowed': 'pets'
        };
        
        return amenityMap[amenity] || amenity.toLowerCase().replace(/\s+/g, '');
    };
    
    // Handle location filter change
    const handleLocationChange = (e) => {
        const locationValue = e.target.value;
        setSelectedLocation(locationValue);
        
        // Apply filter immediately when location is changed
        let filtered = [...properties];
        
        if (locationValue !== "all") {
            const locationLower = locationValue.toLowerCase();
            
            if (locationLower.includes('pattaya') || locationLower.includes('thailand')) {
                filtered = filtered.filter(property => 
                    property.location?.toLowerCase().includes('pattaya') || 
                    property.location?.toLowerCase().includes('thailand')
                );
            } else {
                // Use includes for more flexible matching instead of exact match
                filtered = filtered.filter(property => 
                    property.location?.toLowerCase().includes(locationLower)
                );
            }
        }
        
        // Update URL to reflect location change
        const urlParams = new URLSearchParams(window.location.search);
        
        if (locationValue === "all") {
            urlParams.delete('location');
        } else {
            urlParams.set('location', locationValue);
        }
        
        // Update browser URL without reloading the page
        const newUrl = urlParams.toString() 
            ? `/properties?${urlParams.toString()}` 
            : '/properties';
        
        window.history.pushState(
            { path: newUrl }, 
            '', 
            newUrl
        );
        
        setFilteredProperties(filtered);
        
        // Dispatch custom event to update Hero component
        window.dispatchEvent(new CustomEvent('locationFilterChanged', {
            detail: {
                location: locationValue === "all" ? "" : locationValue,
                count: filtered.length
            }
        }));
    };
    
    // Handle category filter change
    const handleCategoryChange = (e) => {
        const category = e.target.value;
        if (e.target.checked) {
            setSelectedCategories([...selectedCategories, category]);
        } else {
            setSelectedCategories(selectedCategories.filter(c => c !== category));
        }
    };
    
    // Apply filters
    const applyFilters = () => {
        let result = [...properties];
        
        // Filter by location
        if (selectedLocation !== "all") {
            if (selectedLocation === "Pattaya, Thailand") {
                result = result.filter(property => 
                    property.location?.toLowerCase().includes('pattaya') || 
                    property.location?.toLowerCase().includes('thailand')
                );
            } else {
                result = result.filter(property => 
                    property.location?.toLowerCase() === selectedLocation.toLowerCase()
                );
            }
        }
        
        // Filter by amenity categories
        if (selectedCategories.length > 0) {
            result = result.filter(property => {
                return selectedCategories.some(category => {
                    // Handle special cases
                    if (category === 'Special Features' && 
                        (property.isStandalone || property.petFriendly)) {
                        return true;
                    }
                    
                    // Check if property has any amenity in this category
                    return property.amenities?.some(amenity => {
                        const amenityCategory = categorizeAmenity(amenity);
                        return amenityCategory === category;
                    });
                });
            });
        }
        
        // Filter by capacity if adults + children were specified
        if (searchParams.adults > 0 || searchParams.children > 0) {
            const totalGuests = searchParams.adults + searchParams.children;
            result = result.filter(property => {
                // Check if property can accommodate the requested number of guests
                return (property.maxOccupancy && property.maxOccupancy >= totalGuests) ||
                       (property.capacity && property.capacity >= totalGuests);
            });
        }
        
        // Update URL to reflect current filters
        const urlParams = new URLSearchParams();
        
        // Add location to URL if selected (not "all")
        if (selectedLocation !== "all") {
            urlParams.set('location', selectedLocation);
        }
        
        // Add capacity params if they exist in the current searchParams
        if (searchParams.adults > 0) {
            urlParams.set('adults', searchParams.adults);
        }
        
        if (searchParams.children > 0) {
            urlParams.set('children', searchParams.children);
        }
        
        // Add date params if they exist
        if (searchParams.checkIn) {
            urlParams.set('checkIn', searchParams.checkIn);
        }
        
        if (searchParams.checkOut) {
            urlParams.set('checkOut', searchParams.checkOut);
        }
        
        // Add amenity categories to URL if selected
        if (selectedCategories.length > 0) {
            urlParams.set('amenities', selectedCategories.join(','));
        }
        
        // Update browser URL without reloading the page
        const newUrl = urlParams.toString() 
            ? `/properties?${urlParams.toString()}` 
            : '/properties';
        
        window.history.pushState(
            { path: newUrl }, 
            '', 
            newUrl
        );
        
        setFilteredProperties(result);
        
        // Dispatch custom event to update Hero component
        window.dispatchEvent(new CustomEvent('filtersApplied', {
            detail: {
                location: selectedLocation === "all" ? "" : selectedLocation,
                count: result.length,
                totalCount: properties.length
            }
        }));
    };
    
    // Clear all filters
    const clearFilters = () => {
        setSelectedLocation("all");
        setSelectedCategories([]);
        setFilteredProperties(properties);
        
        // Reset URL parameters
        window.history.replaceState({}, '', '/properties');
        
        // Reset UI elements
        document.querySelectorAll('input[name="location"]').forEach(input => {
            input.checked = input.id === "locationAll";
        });
        
        document.querySelectorAll('input[type="checkbox"]').forEach(input => {
            input.checked = false;
        });
        
        // Reset search params
        setSearchParams({
            location: "",
            checkIn: "",
            checkOut: "",
            adults: 0,
            children: 0
        });
        
        // Dispatch a custom event to notify Hero component about filter clearing
        window.dispatchEvent(new CustomEvent('filtersCleared', {
            detail: {
                count: properties.length
            }
        }));
    };

    // Render a message when search parameters are applied
    const renderSearchSummary = () => {
        if (!searchParams.location && searchParams.adults === 0 && searchParams.children === 0 && !searchParams.checkIn) {
            return null;
        }
        
        let summary = "Search results for: ";
        let hasParams = false;
        
        if (searchParams.location) {
            summary += `${searchParams.location}`;
            hasParams = true;
        }
        
        if (searchParams.checkIn && searchParams.checkOut) {
            summary += `${hasParams ? ", " : ""}${new Date(searchParams.checkIn).toLocaleDateString()} to ${new Date(searchParams.checkOut).toLocaleDateString()}`;
            hasParams = true;
        }
        
        if (searchParams.adults > 0 || searchParams.children > 0) {
            summary += `${hasParams ? ", " : ""}${searchParams.adults} Adults, ${searchParams.children} Children`;
            hasParams = true;
        }
        
        return hasParams ? (
            <div className="alert alert-info mb-4">
                {summary} <button onClick={clearFilters} className="btn btn-sm btn-outline-primary ms-2">Clear Filters</button>
            </div>
        ) : null;
    };

    return (
        <div className="container my-5">
            {loading ? (
                // Move loading container outside the row/column structure for full-page centering
                <div className="loading-container">
                    <div className="spinner-border" role="status">
                        <span className="visually-hidden">Loading...</span>
                    </div>
                    <p className="mt-3">Loading properties...</p>
                </div>
            ) : (
                <div className="row">
                    {renderSearchSummary()}
                    
                    {/* Sidebar Filters */}
                    <div className="col-lg-4 filter-section-wrapper">
                        <div className="filter-section">
                            <h3 className="filter-title">Location</h3>
                            <div className="filter-option">
                                <div className="form-check">
                                    <input 
                                        className="form-check-input" 
                                        type="radio" 
                                        name="location" 
                                        id="locationAll" 
                                        value="all"
                                        defaultChecked={!searchParams.location}
                                        onChange={handleLocationChange}
                                    />
                                    <label className="form-check-label" htmlFor="locationAll">All</label>
                                </div>
                            </div>
                            {locations.map((loc, index) => (
                                <div className="filter-option" key={index}>
                                    <div className="form-check">
                                        <input 
                                            className="form-check-input" 
                                            type="radio" 
                                            name="location" 
                                            id={`location${loc.replace(/\s+/g, '').replace(/,/g, '')}`}
                                            value={loc}
                                            defaultChecked={searchParams.location === loc}
                                            onChange={handleLocationChange}
                                        />
                                        <label className="form-check-label" htmlFor={`location${loc.replace(/\s+/g, '').replace(/,/g, '')}`}>
                                            {loc}
                                        </label>
                                    </div>
                                </div>
                            ))}

                            <h3 className="filter-title mt-4">Amenities</h3>
                            {Object.keys(amenityCategories).sort().map((category, categoryIndex) => (
                                <div className="filter-option" key={categoryIndex}>
                                    <div className="form-check">
                                        <input 
                                            className="form-check-input" 
                                            type="checkbox" 
                                            id={`category${category.replace(/\s+/g, '')}`}
                                            value={category}
                                            onChange={handleCategoryChange}
                                        />
                                        <label className="form-check-label" htmlFor={`category${category.replace(/\s+/g, '')}`}>
                                            {category}
                                        </label>
                                    </div>
                                </div>
                            ))}
                            
                            <div className="d-flex justify-content-between mt-4">
                                <button onClick={clearFilters} className="btn btn-link text-primary" style={{ textDecoration: 'none' }}>Clear all</button>
                                <button onClick={applyFilters} className="btn btn-primary">Filter Result</button>
                            </div>
                        </div>
                    </div>

                    {/* Villa Listings */}
                    <div className="col-lg-8">
                        {filteredProperties.length > 0 ? (
                            filteredProperties.map((property) => (
                                <PropertyCard key={property.id} property={property} />
                            ))
                        ) : (
                            <div className="alert alert-info">
                                No properties match your selected filters. Try changing your filters.
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
}

export default VillaList;